import React, { useState } from 'react';
import QRCode from 'qrcode';
import { exportTransferCode, buildTransferUrl } from '../lib/deviceTransfer';
import { COLORS, SPACING } from '../styles/theme';

export function DeviceTransfer({ onClose }: { onClose: () => void }) {
  const [passphrase, setPassphrase] = useState('');
  const [transferUrl, setTransferUrl] = useState('');
  const [textCode, setTextCode] = useState('');
  const [copied, setCopied] = useState<'url' | 'code' | null>(null);
  const [showCode, setShowCode] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const generate = async () => {
    if (passphrase.length < 8) {
      setError('Passphrase must be at least 8 characters.');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const code = await exportTransferCode(passphrase);
      const url = buildTransferUrl(code);
      setTransferUrl(url);
      setTextCode(code);
      const canvas = document.createElement('canvas');
      await QRCode.toCanvas(canvas, url, { width: 220, margin: 2 });
      const container = document.getElementById('qr-container');
      if (container) { container.innerHTML = ''; container.appendChild(canvas); }
    } catch {
      setError('Failed to generate transfer code.');
    }
    setGenerating(false);
  };

  const copy = (type: 'url' | 'code') => {
    navigator.clipboard?.writeText(type === 'url' ? transferUrl : textCode);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div style={{ padding: SPACING.lg, fontFamily: '"IBM Plex Mono", monospace' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACING.lg }}>
        <span style={{ fontSize: '12px', fontWeight: 700, color: COLORS.text }}>Link a New Device</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: '14px' }}>✕</button>
      </div>

      <div style={{ backgroundColor: 'rgba(139,30,45,0.08)', border: `1px solid ${COLORS.accent}`, padding: SPACING.md, marginBottom: SPACING.lg, fontSize: '9px', color: COLORS.text, lineHeight: 1.6 }}>
        Set a passphrase, then scan the QR on your other device. Your account is encrypted — anyone who gets the code still needs the passphrase.
      </div>

      {!transferUrl ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
          <input
            type="password"
            placeholder="Passphrase (min 8 chars)"
            value={passphrase}
            onChange={e => { setPassphrase(e.target.value); setError(''); }}
            style={{ width: '100%', padding: SPACING.sm, backgroundColor: '#fff', color: '#000', border: `2px inset ${COLORS.highlight}`, fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', boxSizing: 'border-box' }}
          />
          {error && <p style={{ margin: 0, fontSize: '9px', color: COLORS.accent }}>{error}</p>}
          <button
            onClick={generate}
            disabled={generating || passphrase.length < 8}
            style={{ padding: SPACING.sm, background: passphrase.length >= 8 ? COLORS.accent : COLORS.panel, color: passphrase.length >= 8 ? '#fff' : COLORS.textMuted, border: `2px outset ${COLORS.highlight}`, cursor: passphrase.length >= 8 ? 'pointer' : 'default', fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px' }}
          >
            {generating ? 'Encrypting...' : 'Generate transfer code'}
          </button>
        </div>
      ) : (
        <>
          <div id="qr-container" style={{ display: 'flex', justifyContent: 'center', marginBottom: SPACING.lg }} />

          <button
            onClick={() => copy('url')}
            style={{ width: '100%', padding: SPACING.sm, marginBottom: SPACING.sm, background: copied === 'url' ? COLORS.accent : COLORS.panel, color: copied === 'url' ? '#fff' : COLORS.text, border: `2px outset ${COLORS.highlight}`, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px' }}
          >
            {copied === 'url' ? 'Link copied' : 'Copy transfer link'}
          </button>

          <button
            onClick={() => setShowCode(v => !v)}
            style={{ width: '100%', padding: SPACING.sm, background: 'none', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: '9px', textDecoration: 'underline', fontFamily: '"IBM Plex Mono", monospace' }}
          >
            {showCode ? 'Hide' : "Can't scan? Use text code instead"}
          </button>

          {showCode && (
            <div style={{ marginTop: SPACING.sm, border: `2px inset ${COLORS.highlight}`, padding: SPACING.sm, backgroundColor: '#fff' }}>
              <p style={{ margin: `0 0 ${SPACING.xs} 0`, fontSize: '8px', color: COLORS.textMuted }}>Paste this on your other device under "Import account". The passphrase you set is also required.</p>
              <div style={{ fontSize: '8px', color: '#000', wordBreak: 'break-all', marginBottom: SPACING.xs, maxHeight: 60, overflowY: 'auto' }}>{textCode}</div>
              <button
                onClick={() => copy('code')}
                style={{ background: copied === 'code' ? COLORS.accent : 'transparent', color: copied === 'code' ? '#fff' : COLORS.accent, border: `1px solid ${COLORS.accent}`, padding: `${SPACING.xs} ${SPACING.sm}`, cursor: 'pointer', fontSize: '8px', fontFamily: '"IBM Plex Mono", monospace' }}
              >
                {copied === 'code' ? 'Copied' : 'Copy code'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
